import React from 'react';

import { gql, useLazyQuery } from '@apollo/client';
import { map, size } from 'lodash';
import { AppContext } from '../contexts/app_context';
import { useGetPermissions } from '../components';

const getRestaurantsGql = gql(`
  query getRestaurants {
    user {
      id
      restaurants {
        id
        name
        branchName
        businessType
        frontPhotoUri
        isOnGoingJobs
        isInfoCompletedV2
      }
    }
  }
`);

const useLoadRestaurantsContext = ({ skip }: { skip?: boolean } = {}) => {
  const { setContextRestaurants, setContextPermissions, contextUser } = React.useContext(AppContext);
  const { getChainPermissions, getRestaurantPermissions, getUserChainRestaurantsPermissions } = useGetPermissions();

  const [isLoading, setIsLoading] = React.useState(true);
  const [getRestaurantsQuery, { loading }] = useLazyQuery(getRestaurantsGql, {
    fetchPolicy: 'network-only',
  });

  const fetchRestaurantsAndPermissions = React.useCallback(
    async (updateContext = true) => {
      const { data } = await getRestaurantsQuery();
      let nextRestaurants = [];
      if (data?.user.restaurants) {
        nextRestaurants = map(data.user.restaurants, ({ isOnGoingJobs, isInfoCompletedV2, ...restaurant }) => ({
          ...restaurant,
          isInfoComplete: isInfoCompletedV2,
          isHiring: isOnGoingJobs,
        }));
        if (updateContext) {
          setContextRestaurants(nextRestaurants);
        }
      }

      if (contextUser?.chain?.id) {
        const chainPermissions = await getChainPermissions(contextUser.chain.id);
        if (chainPermissions?.chain) {
          setContextPermissions((prev) => ({ ...prev, chain: chainPermissions.chain }));
        }
      }

      if (nextRestaurants.length > 0) {
        const restaurantPermissions = await getRestaurantPermissions(nextRestaurants[0].id);
        if (restaurantPermissions?.restaurant) {
          setContextPermissions((prev) => ({ ...prev, restaurant: restaurantPermissions.restaurant }));
        }
      }

      // New source - every restaurant's permission names in one query, so Protector reacts instantly on restaurant switch (no per-switch refetch).
      const restaurantsPermissions = await getUserChainRestaurantsPermissions();
      if (size(restaurantsPermissions) > 0) {
        setContextPermissions((prev) => ({ ...prev, restaurants: restaurantsPermissions }));
      }

      return nextRestaurants;
    },
    [contextUser?.chain?.id, getRestaurantsQuery, setContextRestaurants],
  );

  React.useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      await fetchRestaurantsAndPermissions();
      setIsLoading(false);
    };

    if (!skip) {
      fetch();
    } else {
      setIsLoading(false);
    }
  }, [fetchRestaurantsAndPermissions, skip]);

  return { loading: loading || isLoading, fetchRestaurantsAndPermissions };
};

export { useLoadRestaurantsContext };
